/**
 * PageEditContext — per-page "which widget is in inline edit mode" signal.
 *
 * Scoped to a single PageNode (provided around its grid body), NOT the whole
 * canvas: tap-to-edit lives inside one page's grid, and the only consumer is a
 * widget that needs to (a) announce it entered edit mode and (b) let the page
 * suppress grid item-drag while it's editing.
 *
 * Why it exists: a native rich-text widget (UITextView/EditText) scrolls and
 * has draggable selection handles. It sits inside a GridItem whose Pan gesture
 * activates after ~10px of travel — so a selection-drag/scroll inside the text
 * view would otherwise activate item-drag and yank the widget mid-edit. While a
 * widget is editing we force the page grid into preview (disabling every item's
 * Pan), exactly like the AddDrag suppression already does.
 *
 * Holds NO persisted state (transient render-only flag, never written to Yjs).
 * Inert outside a provider so widgets that render without one (web, previews)
 * are unaffected.
 */
import { createContext, useContext } from "react";

export interface PageEdit {
  /** The item id currently in inline edit mode on this page, or null. */
  readonly editingItemId: string | null;
  /** Enter (id) / leave (null) inline edit for an item on this page. */
  readonly setEditingItemId: (id: string | null) => void;
}

const INERT: PageEdit = {
  editingItemId: null,
  setEditingItemId: () => {},
};

const PageEditContext = createContext<PageEdit | null>(null);

export const PageEditProvider = PageEditContext.Provider;

export function usePageEdit(): PageEdit {
  return useContext(PageEditContext) ?? INERT;
}
